import React, { useState, useEffect, useCallback, useRef } from "react";
import { CompanyAdminLayout } from "../../../components/CompanyAdmin/CompanyAdminLayout";
import TextInput from "../../../components/UI/TextInput";
import { Button } from "../../../components/UI/Button";
import { useAuthStore } from "../../../stores/auth-store";
import { companyOrderRevisionRequestUseCase } from "di/container";
import type {
  OrderRevisionBoardItem,
  OrderRevisionMessage,
  OrderRevisionSession,
  OrderRevisionRequestCategory,
} from "domain/types/order-revision-request";
import {
  OrderRevisionRequestCategoryColors,
  OrderRevisionRequestCategoryLabels,
} from "domain/types/order-revision-request";

const OrderRevisionRequestManagement: React.FC = () => {
  const { user } = useAuthStore();
  const companyId = user?.companyId;

  const [boardItems, setBoardItems] = useState<OrderRevisionBoardItem[]>([]);
  const [selectedSessionId, setSelectedSessionId] = useState<number | null>(null);
  const [session, setSession] = useState<OrderRevisionSession | null>(null);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 필터
  const [categoryFilter, setCategoryFilter] = useState<
    OrderRevisionRequestCategory | ""
  >("");
  const [closedFilter, setClosedFilter] = useState<boolean | "">("");
  const [keyword, setKeyword] = useState("");

  // 페이징
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  // 메시지 수정
  const [editingMessageId, setEditingMessageId] = useState<number | null>(null);
  const [editMessageDraft, setEditMessageDraft] = useState("");
  const [isEditSubmitting, setIsEditSubmitting] = useState(false);

  // 채팅 입력
  const [newMessageDraft, setNewMessageDraft] = useState("");
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [isClosingSession, setIsClosingSession] = useState(false);
  const [isReopeningSession, setIsReopeningSession] = useState(false);
  const [chatVisible, setChatVisible] = useState(false);

  // 메시지 우클릭 컨텍스트 메뉴
  const [messageContextMenu, setMessageContextMenu] = useState<{
    x: number;
    y: number;
    message: OrderRevisionMessage;
  } | null>(null);

  // 메시지 영역 자동 스크롤
  const messagesScrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (messagesScrollRef.current) {
      messagesScrollRef.current.scrollTop = messagesScrollRef.current.scrollHeight;
    }
  }, [session?.id, session?.messages.length]);

  // 메시지 컨텍스트 메뉴 닫기
  useEffect(() => {
    if (!messageContextMenu) return;
    const close = () => setMessageContextMenu(null);
    const closeOnEscape = (e: KeyboardEvent) => { if (e.key === "Escape") close(); };
    window.addEventListener("click", close);
    window.addEventListener("scroll", close, true);
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      window.removeEventListener("click", close);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [messageContextMenu]);

  const fetchBoardItems = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await companyOrderRevisionRequestUseCase.getOrderRevisionSessions({
        companyId,
        category: categoryFilter || undefined,
        closed: closedFilter === "" ? undefined : closedFilter,
        keyword: keyword || undefined,
        page,
        size: 20,
      });
      const group = (item: OrderRevisionBoardItem) =>
        item.closedAt ? 2 : item.unreadCountForAdmin > 0 ? 0 : 1;
      const items = [...(result?.content || [])].sort((a, b) => {
        const gDiff = group(a) - group(b);
        if (gDiff !== 0) return gDiff;
        // 미답변(group=0): 오래된 것 위 (ascending) — 가장 오래 기다린 문의가 제일 위
        if (group(a) === 0) return new Date(a.latestActivityAt).getTime() - new Date(b.latestActivityAt).getTime();
        return new Date(b.latestActivityAt).getTime() - new Date(a.latestActivityAt).getTime();
      });
      setBoardItems(items);
      setTotalPages(result?.totalPages || 0);
      setTotalElements(result?.totalElements || 0);
    } catch (err: any) {
      setError(err.message || "목록을 불러오는데 실패했습니다.");
    } finally {
      setLoading(false);
    }
  }, [companyId, categoryFilter, closedFilter, keyword, page]);

  useEffect(() => { fetchBoardItems(); }, [fetchBoardItems]);

  const handleSelectOrder = async (sessionId: number) => {
    if (!companyId) return;
    setDetailLoading(true);
    setEditingMessageId(null);
    try {
      const detail = await companyOrderRevisionRequestUseCase.getOrderRevisionSessionDetail({
        companyId,
        sessionId,
      });
      setSession(detail);
      setSelectedSessionId(sessionId);
      setChatVisible(true);
    } catch (err: any) {
      setError(err.message || "문의 내역을 불러오는데 실패했습니다.");
    } finally {
      setDetailLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!companyId || selectedSessionId === null) return;
    const content = newMessageDraft.trim();
    if (!content) return;
    setIsSendingMessage(true);
    try {
      const updated = await companyOrderRevisionRequestUseCase.sendAdminMessage({
        companyId,
        sessionId: selectedSessionId,
        request: { replyContent: content },
      });
      setSession(updated);
      setNewMessageDraft("");
      fetchBoardItems();
    } catch (err: any) {
      alert(err.message || "메시지 전송에 실패했습니다.");
    } finally {
      setIsSendingMessage(false);
    }
  };

  const handleStartEditMessage = (message: OrderRevisionMessage) => {
    setEditingMessageId(message.id);
    setEditMessageDraft(message.content);
  };

  const handleCancelEditMessage = () => {
    setEditingMessageId(null);
    setEditMessageDraft("");
  };

  const handleUpdateMessage = async () => {
    if (!companyId || editingMessageId === null) return;
    const content = editMessageDraft.trim();
    if (!content) { alert("내용을 입력해주세요."); return; }
    setIsEditSubmitting(true);
    try {
      const updated = await companyOrderRevisionRequestUseCase.updateAdminMessage({
        companyId,
        messageId: editingMessageId,
        request: { replyContent: content },
      });
      setSession(updated);
      setEditingMessageId(null);
      setEditMessageDraft("");
    } catch (err: any) {
      alert(err.message || "메시지 수정에 실패했습니다.");
    } finally {
      setIsEditSubmitting(false);
    }
  };

  const handleDeleteMessage = async (messageId: number) => {
    if (!companyId) return;
    if (!window.confirm("이 메시지를 삭제하시겠습니까?")) return;
    try {
      const updated = await companyOrderRevisionRequestUseCase.deleteAdminMessage({
        companyId,
        messageId,
      });
      setSession(updated);
      fetchBoardItems();
    } catch (err: any) {
      alert(err.message || "메시지 삭제에 실패했습니다.");
    }
  };

  const handleReopenSession = async () => {
    if (!companyId || selectedSessionId === null) return;
    setIsReopeningSession(true);
    try {
      const updated = await companyOrderRevisionRequestUseCase.reopenSession({
        companyId,
        sessionId: selectedSessionId,
      });
      setSession(updated);
      fetchBoardItems();
    } catch (err: any) {
      alert(err.message || "되돌리기에 실패했습니다.");
    } finally {
      setIsReopeningSession(false);
    }
  };

  const handleCloseSession = async (needsRework: boolean) => {
    if (!companyId || selectedSessionId === null) return;
    const label = needsRework ? "재작업 결정" : "문의 종료";
    if (!window.confirm(`이 문의를 "${label}"으로 처리하시겠습니까?`)) return;
    setIsClosingSession(true);
    try {
      const updated = await companyOrderRevisionRequestUseCase.closeSession({
        companyId,
        sessionId: selectedSessionId,
        needsRework,
      });
      setSession(updated);
      fetchBoardItems();
    } catch (err: any) {
      alert(err.message || "처리에 실패했습니다.");
    } finally {
      setIsClosingSession(false);
    }
  };

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleString("ko-KR", {
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });

  const formatDateOnly = (dateString?: string | null) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("ko-KR", {
      year: "numeric", month: "2-digit", day: "2-digit",
    });
  };

  const renderCategoryBadge = (category: OrderRevisionRequestCategory) => {
    const colors = OrderRevisionRequestCategoryColors[category];
    return (
      <span
        className="px-2 py-1 rounded text-xs font-medium whitespace-nowrap"
        style={{ backgroundColor: colors.bg, color: colors.text }}
      >
        {OrderRevisionRequestCategoryLabels[category]}
      </span>
    );
  };

  const renderSessionBadge = (
    closedAt: string | null | undefined,
    unreadCount: number
  ) => {
    if (closedAt) {
      return (
        <span className="px-2 py-1 rounded text-xs font-medium whitespace-nowrap bg-[#f5f5f5] text-[#888]">
          답변완료
        </span>
      );
    }
    if (unreadCount > 0) {
      return (
        <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-xs font-bold text-white bg-[#F24A4A]">
          {unreadCount}
        </span>
      );
    }
    return null;
  };

  // 왼쪽 목록 패널
  const renderListPanel = () => (
    <>
      <div className="flex-shrink-0 p-3 border-b border-[#e0e0e0] space-y-2">
        <div className="flex gap-2">
          <select
            className="flex-1 h-9 px-2 border border-[#e0e0e0] rounded-lg text-xs bg-white min-w-0"
            value={categoryFilter}
            onChange={(e) => {
              setCategoryFilter(e.target.value as OrderRevisionRequestCategory | "");
              setPage(0);
            }}
          >
            <option value="">전체 카테고리</option>
            <option value="GENERAL_INQUIRY">일반문의</option>
            <option value="REVISION_REQUEST">수정요청</option>
          </select>
          <select
            className="flex-1 h-9 px-2 border border-[#e0e0e0] rounded-lg text-xs bg-white min-w-0"
            value={closedFilter === "" ? "" : closedFilter ? "true" : "false"}
            onChange={(e) => {
              const v = e.target.value;
              setClosedFilter(v === "" ? "" : v === "true");
              setPage(0);
            }}
          >
            <option value="">전체 상태</option>
            <option value="false">진행중</option>
            <option value="true">답변완료</option>
          </select>
        </div>
        <div className="flex gap-2">
          <TextInput
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter") { setPage(0); fetchBoardItems(); }
            }}
            className="flex-1 min-w-0"
            innerClassName="h-9 text-xs"
            placeholder="상품명 검색"
          />
          <Button
            className="flex-shrink-0 whitespace-nowrap !h-9 !text-xs !px-3"
            onClick={() => { setPage(0); fetchBoardItems(); }}
          >
            검색
          </Button>
        </div>
        <div className="text-xs text-[#888]">
          총 <span className="font-medium text-[#222]">{totalElements}</span>개 주문
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto">
        {loading ? (
          <div className="px-4 py-8 text-center text-sm text-[#888]">불러오는 중...</div>
        ) : boardItems.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-[#888]">주문별 문의가 없습니다.</div>
        ) : (
          <>
            {!chatVisible && (
              <div className="flex items-center gap-4 px-4 py-2 border-b border-[#f0f0f0] bg-[#FAFAFA] text-xs text-[#aaa] font-medium sticky top-0">
                <span className="flex-1 min-w-0">상품명</span>
                <span className="w-20 flex-shrink-0">카테고리</span>
                <span className="w-28 flex-shrink-0">고객</span>
                <span className="w-28 flex-shrink-0">주문일</span>
                <span className="w-36 flex-shrink-0">최근 활동</span>
              </div>
            )}
            {boardItems.map((item) => (
              <button
                key={item.id}
                onClick={() => handleSelectOrder(item.id)}
                className={`w-full text-left border-b border-[#f0f0f0] hover:bg-[#FAFAFA] ${
                  selectedSessionId === item.id ? "bg-[#E8EFFF]" : "bg-white"
                } ${chatVisible ? "flex flex-col px-4 py-3" : "flex items-center gap-4 px-4 py-3"}`}
              >
                {chatVisible ? (
                  <>
                    <div className="flex items-center justify-between gap-2 min-w-0 w-full">
                      <span className="text-sm font-medium text-[#222] flex-1 min-w-0">{item.productName}</span>
                      {renderSessionBadge(item.closedAt, item.unreadCountForAdmin)}
                    </div>
                    <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                      {renderCategoryBadge(item.category)}
                      <span className="text-xs text-[#555]">{item.purchaserName || "-"}</span>
                      <span className="text-xs text-[#888]">· {formatDateOnly(item.orderCreatedAt)}</span>
                      <span className="text-xs text-[#aaa]">· {formatDate(item.latestActivityAt)}</span>
                    </div>
                  </>
                ) : (
                  <>
                    <span className="flex-1 min-w-0 flex items-center gap-2">
                      <span className="text-sm font-medium text-[#222] truncate min-w-0">{item.productName}</span>
                      {renderSessionBadge(item.closedAt, item.unreadCountForAdmin)}
                    </span>
                    <span className="w-20 flex-shrink-0">{renderCategoryBadge(item.category)}</span>
                    <span className="w-28 flex-shrink-0 text-xs text-[#555] truncate">{item.purchaserName || "-"}</span>
                    <span className="w-28 flex-shrink-0 text-xs text-[#888]">{formatDateOnly(item.orderCreatedAt)}</span>
                    <span className="w-36 flex-shrink-0 text-xs text-[#aaa]">{formatDate(item.latestActivityAt)}</span>
                  </>
                )}
              </button>
            ))}
          </>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex-shrink-0 flex items-center justify-center gap-2 p-2 border-t border-[#e0e0e0]">
          <button
            className="px-2 py-1 border border-[#e0e0e0] rounded text-xs disabled:opacity-50"
            disabled={page === 0}
            onClick={() => setPage((p) => p - 1)}
          >
            이전
          </button>
          <span className="px-2 text-xs text-[#555]">{page + 1} / {totalPages}</span>
          <button
            className="px-2 py-1 border border-[#e0e0e0] rounded text-xs disabled:opacity-50"
            disabled={page >= totalPages - 1}
            onClick={() => setPage((p) => p + 1)}
          >
            다음
          </button>
        </div>
      )}
    </>
  );

  // 메시지 말풍선
  const renderMessageBubble = (message: OrderRevisionMessage) => {
    const isAdmin = message.senderType === "ADMIN";

    if (isAdmin && editingMessageId === message.id) {
      return (
        <div key={message.id} className="flex justify-end mb-1.5">
          <div className="max-w-[70%] w-full">
            <div className="border border-dashed border-[#4285F4] rounded-2xl rounded-tr-sm px-4 py-3 bg-[#F5F8FF]">
              <textarea
                className="w-full px-2 py-1 border-none text-sm bg-transparent focus:outline-none resize-none"
                rows={3}
                value={editMessageDraft}
                onChange={(e) => setEditMessageDraft(e.target.value)}
              />
              <div className="flex justify-end gap-2 mt-1">
                <button
                  className="px-4 py-1.5 text-xs text-[#555] border border-[#e0e0e0] rounded-lg bg-white hover:bg-[#f5f5f5]"
                  onClick={handleCancelEditMessage}
                >
                  취소
                </button>
                <button
                  className="px-4 py-1.5 bg-[#4285F4] text-white rounded-lg text-xs hover:bg-[#3367D6] disabled:opacity-50"
                  onClick={handleUpdateMessage}
                  disabled={isEditSubmitting}
                >
                  {isEditSubmitting ? "처리 중..." : "수정 완료"}
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (isAdmin) {
      return (
        <div key={message.id} className="flex justify-end mb-1.5">
          <div className="max-w-[70%] flex items-end gap-2">
            <span className="text-xs text-[#aaa] whitespace-nowrap flex-shrink-0">
              {formatDate(message.createdAt)}
            </span>
            <div
              className="bg-[#4285F4] rounded-2xl rounded-tr-sm px-4 py-2.5 cursor-context-menu"
              onContextMenu={(e) => {
                e.preventDefault();
                setMessageContextMenu({ x: e.clientX, y: e.clientY, message });
              }}
            >
              <div className="text-sm text-white whitespace-pre-wrap">{message.content}</div>
              {message.editedAt && <div className="text-xs text-[#E8EFFF] mt-1">(수정됨)</div>}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div key={message.id} className="flex justify-start mb-1.5">
        <div className="max-w-[70%] flex items-end gap-2">
          <div className="bg-white border border-[#e0e0e0] rounded-2xl rounded-tl-sm px-4 py-2.5">
            <div className="text-sm text-[#222] whitespace-pre-wrap">{message.content}</div>
            {message.editedAt && <div className="text-xs text-[#B45309] mt-1">(수정됨)</div>}
          </div>
          <span className="text-xs text-[#aaa] whitespace-nowrap flex-shrink-0">
            {formatDate(message.createdAt)}
          </span>
        </div>
      </div>
    );
  };

  // 오른쪽 채팅 패널
  const renderChatPanel = () => {
    if (!session) return null;

    return (
      <>
        {/* 헤더 */}
        <div className="flex-shrink-0 px-5 py-4 border-b border-[#e0e0e0]">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className="text-base font-bold text-[#222]">
                {session.productName}
              </div>
              <div className="flex items-center gap-2 flex-wrap mt-1">
                {renderCategoryBadge(session.category)}
                <span className="text-xs text-[#888]">
                  {session.orderNumber || "-"} · {session.purchaserName || "-"} ({session.purchaserEmail || "-"})
                </span>
              </div>
            </div>
            <button
              className="flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-lg text-[#aaa] hover:bg-[#f5f5f5] hover:text-[#555]"
              onClick={() => setChatVisible(false)}
              aria-label="채팅 닫기"
            >
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M1 1l10 10M11 1L1 11" />
              </svg>
            </button>
          </div>
        </div>

        {/* 메시지 스레드 */}
        <div
          ref={messagesScrollRef}
          className="flex-1 min-h-0 overflow-y-auto px-5 py-4 bg-[#FAFAFA] text-left flex flex-col justify-end"
        >
          {detailLoading ? (
            <div className="px-4 py-8 text-center text-[#888]">불러오는 중...</div>
          ) : (
            session.messages.map((message) => renderMessageBubble(message))
          )}
        </div>

        {/* 하단 — 종료 여부에 따라 분기 */}
        {session.closedAt ? (
          <div className="flex-shrink-0 border-t border-[#e0e0e0] px-5 py-4 bg-[#FAFAFA] text-center">
            {session.needsRework ? (
              <>
                <span className="inline-block px-3 py-1 rounded-full bg-[#FEF3C7] text-[#B45309] text-xs font-semibold mb-2">
                  재작업 결정
                </span>
                <p className="text-sm text-[#888]">재작업이 결정된 문의입니다.</p>
              </>
            ) : (
              <p className="text-sm text-[#888]">문의가 종료되었습니다.</p>
            )}
            <p className="text-xs text-[#BBBBBB] mt-1">
              고객이 추가 문의를 남기면 목록에서 확인하실 수 있습니다.
            </p>
            <button
              className="mt-2 text-xs text-[#BBBBBB] hover:text-[#888] hover:underline disabled:opacity-50"
              onClick={handleReopenSession}
              disabled={isReopeningSession}
            >
              {isReopeningSession ? "처리 중..." : "되돌리기"}
            </button>
          </div>
        ) : (
          <div className="flex-shrink-0 border-t border-[#e0e0e0]">
            {/* 액션 버튼 행 */}
            <div className="flex justify-end items-center gap-2 px-4 py-2 border-b border-[#f0f0f0] bg-[#FAFAFA]">
              {session.category === "REVISION_REQUEST" && (
                <button
                  className="px-3 py-1.5 text-xs text-[#B45309] border border-[#D97706]/40 rounded-lg hover:bg-[#FEF3C7] disabled:opacity-50 whitespace-nowrap"
                  onClick={() => handleCloseSession(true)}
                  disabled={isClosingSession}
                >
                  재작업 결정
                </button>
              )}
              <button
                className="px-3 py-1.5 text-xs text-[#555] border border-[#e0e0e0] rounded-lg hover:bg-[#f0f0f0] disabled:opacity-50 whitespace-nowrap"
                onClick={() => handleCloseSession(false)}
                disabled={isClosingSession}
              >
                문의 종료
              </button>
            </div>
            {/* 메시지 입력 행 */}
            <div className="flex items-end gap-2 px-4 py-3">
              <textarea
                className="flex-1 min-w-0 px-3 py-2 border border-[#e0e0e0] rounded-xl text-sm focus:border-[#4285F4] focus:outline-none resize-none"
                rows={2}
                placeholder="메시지를 입력하세요 (Enter 전송, Shift+Enter 줄바꿈)"
                value={newMessageDraft}
                onChange={(e) => setNewMessageDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
              />
              <button
                className="px-5 py-2.5 bg-[#4285F4] text-white rounded-xl text-sm hover:bg-[#3367D6] disabled:opacity-50 flex-shrink-0"
                onClick={handleSendMessage}
                disabled={isSendingMessage || !newMessageDraft.trim()}
              >
                {isSendingMessage ? "전송 중..." : "전송"}
              </button>
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <CompanyAdminLayout>
      <div className="h-full flex flex-col bg-[#f5f5f5]">
        <div className="flex-shrink-0 bg-white border-b border-[#e0e0e0] px-6 py-4 text-left">
          <h1 className="text-xl font-bold text-[#222]">주문별 문의 관리</h1>
          <p className="text-sm text-[#888] mt-1">
            고객이 남긴 일반문의·수정요청을 확인하고 답변을 등록할 수 있습니다.
          </p>
        </div>

        {error && (
          <div className="flex-shrink-0 mx-6 mt-4 p-3 bg-[#FEF2F2] text-[#F24A4A] text-sm rounded-lg">
            {error}
          </div>
        )}

        <div className="flex-1 min-h-0 flex gap-3 p-3">
          {/* 좌측 목록 패널 */}
          <div
            className="bg-white border border-[#e0e0e0] rounded-2xl flex flex-col overflow-hidden text-left transition-all duration-300"
            style={{ width: chatVisible ? "340px" : "100%", flexShrink: chatVisible ? 0 : 1 }}
          >
            {renderListPanel()}
          </div>

          {/* 우측 채팅 패널 — 세션 선택 시에만 표시 */}
          {chatVisible && selectedSessionId !== null && (
            <div className="flex-1 min-w-0 bg-white border border-[#e0e0e0] rounded-2xl flex flex-col overflow-hidden text-left">
              {renderChatPanel()}
            </div>
          )}
        </div>

        {/* 메시지 우클릭 메뉴 */}
        {messageContextMenu && (
          <div
            className="fixed z-50 bg-white border border-[#e0e0e0] rounded-lg shadow-lg py-1 min-w-[100px] text-left"
            style={{ left: messageContextMenu.x, top: messageContextMenu.y }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="w-full text-left px-4 py-2 text-sm text-[#222] hover:bg-[#f5f5f5]"
              onClick={() => {
                handleStartEditMessage(messageContextMenu.message);
                setMessageContextMenu(null);
              }}
            >
              수정
            </button>
            <button
              className="w-full text-left px-4 py-2 text-sm text-[#F24A4A] hover:bg-[#f5f5f5]"
              onClick={() => {
                handleDeleteMessage(messageContextMenu.message.id);
                setMessageContextMenu(null);
              }}
            >
              삭제
            </button>
          </div>
        )}
      </div>
    </CompanyAdminLayout>
  );
};

export default OrderRevisionRequestManagement;
